import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useOrganization } from '../../../utils/OrganizationContext';
import { useAuth } from '../../../utils/Auth';
import Avatar from '../../../components/Avatar';
import { cardVariants } from '../variants';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { activeOrgId, liquidQRs } = useAuth();
  const { organizations, currentUserOrgData, orgMembers, orgMetadata } = useOrganization();

  const currentOrg = organizations?.find(org => org.id === activeOrgId);
  const isPersonalOrg = currentOrg?.metadata?.type === 'personal';
  const isAccountExpired = !isPersonalOrg && (orgMetadata?.active === false || currentOrg?.metadata?.active === false);

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Check if organization is at or over team capacity
  const currentMemberCount = orgMembers?.length || 0;
  const teamSizeLimit = currentOrg?.plan?.team_size || 0;
  const isAtCapacity = currentMemberCount >= teamSizeLimit;
  const expiredTitle = 'Account expired. Visit Organization Settings to reup your subscription.';

  const orgAvatar = currentOrg?.metadata?.avatar_image_url || null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <motion.div 
          className={styles.header}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className={styles.headerContent}>
            <div className={styles.headerWithAvatar}>
              <Avatar 
                src={orgAvatar} 
                orgName={currentOrg?.name} 
                size="medium"
                alt={`${currentOrg?.name} avatar`}
              />
              <div className={styles.headerText}>
                <h1 className={styles.title}>Organization Dashboard</h1>
                {currentOrg?.name && (
                  <p className={styles.orgName}>Managing: <strong>{currentOrg.name}</strong></p>
                )}
              </div>
            </div>
          </div>
          <motion.button
            className={styles.createOrgButton}
            onClick={() => !isAccountExpired && handleNavigate('/organizations/create')}
            variants={cardVariants}
            animate="animate"
            initial="initial"
            whileHover={!isAccountExpired ? 'hover' : undefined}
            transition={{ duration: 0.1 }}
            title={isAccountExpired ? expiredTitle : 'Create New Organization'}
            disabled={isAccountExpired}
          >
             <i className='fas fa-plus'></i>
             Organization
          </motion.button>
        </motion.div>

        {isAccountExpired && (
          <div className={styles.expiredBanner}>
            This organization account is expired. Visit Organization Settings as an Admin to reup your subscription.
          </div>
        )}

        <motion.div
          className={styles.quickView}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className={styles.quickViewItem}>
            <div className={styles.quickViewLabel}>Team Members</div>
            <div className={styles.quickViewValue}>
              {orgMembers?.length || 0} / {currentOrg?.plan?.team_size || 0}
            </div>
          </div>
          <div className={styles.quickViewDivider}></div>
          <div className={styles.quickViewItem}>
            <div className={styles.quickViewLabel}>Liquid QRs</div>
            <div className={styles.quickViewValue}>
              {liquidQRs?.length || 0} / {currentOrg?.plan?.liquid_qrs || 0}
            </div>
          </div>
        </motion.div>

        <div className={styles.grid}>
          <motion.div
            className={`${styles.card} ${isAccountExpired ? styles.cardDisabled : ''}`}
            onClick={() => !isAccountExpired && handleNavigate('/collection')}
            variants={cardVariants}
            initial="initial"
            animate="animate"
            whileHover={!isAccountExpired ? 'hover' : undefined}
            transition={{ duration: 0.3, delay: 0.1 }}
            title={isAccountExpired ? expiredTitle : ''}
          >
            <div className={styles.icon}>
              <i className='fas fa-qrcode'></i>
            </div>
            <h2 className={styles.cardTitle}>QR Collection</h2>
            <p className={styles.cardDescription}>
              View and manage all QR codes for this organization.
            </p>
          </motion.div>

          <motion.div 
            className={`${styles.card} ${isAccountExpired ? styles.cardDisabled : ''}`}
            onClick={() => !isAccountExpired && handleNavigate('/organizations/members')}
            variants={cardVariants}
            initial="initial"
            animate="animate"
            whileHover={!isAccountExpired ? 'hover' : undefined}
            transition={{ duration: 0.3, delay: 0.3 }}
            title={isAccountExpired ? expiredTitle : ''}
          >
            <div className={styles.icon}>
              <i className='fas fa-users'></i>
            </div>
            <h2 className={styles.cardTitle}>Manage Members</h2>
            <p className={styles.cardDescription}>
              View, manage roles, and remove team members from your organization.
            </p>
          </motion.div>

          <motion.div 
            className={`${styles.card} ${isAtCapacity || isAccountExpired ? styles.cardDisabled : ''}`}
            onClick={() => !isAtCapacity && !isAccountExpired && handleNavigate('/organizations/members/invite')}
            variants={cardVariants}
            initial="initial"
            animate="animate"
            whileHover={!isAtCapacity && !isAccountExpired ? "hover" : undefined}
            transition={{ duration: 0.3, delay: 0.4 }}
            title={isAccountExpired ? expiredTitle : (isAtCapacity ? "Team capacity reached. Upgrade your plan to invite more members." : "")}
          >
            <div className={styles.icon}>
              <i className='fas fa-envelope'></i>
            </div>
            <h2 className={styles.cardTitle}>Invite Member</h2>
            <p className={styles.cardDescription}>
              {isAtCapacity ? (
                <>Team capacity reached. Upgrade to invite more members.</>
              ) : (
                <>Send invitations to new members to join your organization.</>
              )}
            </p>
          </motion.div>
		  {currentUserOrgData?.role === 'admin' && (
			<motion.div
				className={styles.card}
				onClick={() => handleNavigate('/organizations/settings')}
				variants={cardVariants}
				initial='initial'
				animate='animate'
				whileHover='hover'
				transition={{ duration: 0.3, delay: 0.5 }}
			>
				<div className={styles.icon}>
					<i className='fas fa-cog' />
				</div>
				<h2 className={styles.cardTitle}>Organization Settings</h2>
				<p className={styles.cardDescription}>
					Assign admins, transfer organization ownership, update payment.
				</p>
			</motion.div>
		  )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
